import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { Box, Grid, Link, Typography } from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import firstPageBg from "../../Assets/images/firstPagebackground.png";
import caminhoProduto from "../../Assets/images/caminhoProduto.png";
import PrimeiraPagina from "./components/PrimeiraPagina";
import SobreDistribuidora from "./components/SobreDistribuidora";
import ApresentacaoDistribuidora from "./components/ApresentacaoDistribuidora";
import RastreioProduto from "./components/RastreioProduto";
import CaminhoProduto from "./components/CaminhoProduto";
import { RastreioService } from "../../service/rastreio_service";

/**
 * Portal público de rastreabilidade do distribuidor.
 *
 * A estrutura espelha a LandingPage do sisagri-campo-frontend — seções de tela
 * cheia com âncoras e setas fixas de navegação, mesma paleta — porque para quem
 * lê o QR Code os dois têm de parecer o mesmo produto. O que muda são os dados:
 * lá é propriedade/safra/colheita, aqui é distribuidora/lote/ordem de produção.
 *
 * Quem chega aqui não tem login: a rota fica fora do RequireAuth.
 */

const styleFirstPage = {
	p: { xs: "5px", sm: "25px", md: "50px" },
	pt: { xs: "20px", sm: "40px", md: "80px" },
	height: "100vh",
	bgcolor: "#00A69A",
	backgroundImage: `url(${firstPageBg})`,
	backgroundSize: "cover",
	backgroundPosition: "center",
};

const styleBrancoRadial = {
	p: { xs: "5px", sm: "25px", md: "50px" },
	pt: { xs: "20px", sm: "40px", md: "80px" },
	height: "100%",
	minHeight: "100vh",
	bgcolor: "rgba(255,255,255,1)",
	background:
		"radial-gradient(ellipse at center, rgba(255,255,255,1) 0%, rgba(248,248,248,1) 35%, rgba(246,246,246,1) 47%, rgba(173,173,173,1) 100%)",
	color: "black",
};

const styleRastreioProduto = {
	...styleBrancoRadial,
	px: { xs: "5px", sm: "25px", md: "50px" },
	pt: 0,
	display: "flex",
};

const styleCaminhoProduto = {
	p: { xs: "5px", sm: "25px", md: "50px" },
	pt: { xs: "20px", sm: "40px", md: "80px" },
	height: "100%",
	minHeight: "100vh",
	backgroundImage: `url(${caminhoProduto})`,
	backgroundRepeat: "no-repeat",
	backgroundPosition: "center",
	objectFit: "cover",
	color: "black",
};

const styleApresentacao = {
	...styleBrancoRadial,
	px: { xs: "5px", sm: "25px", md: "50px" },
	pt: 0,
};

function Rastreabilidade() {
	const url = new URLSearchParams(window.location.search);
	const codigo = url.get("id") ?? url.get("Id");

	const [dados, setDados] = useState(null);
	const [erro, setErro] = useState(null);
	const [posicaoY, setPosicaoY] = useState(0);
	const [secao, setSecao] = useState(0);

	useEffect(() => {
		const aoRolar = () => setPosicaoY(window.scrollY);
		window.addEventListener("scroll", aoRolar);
		return () => window.removeEventListener("scroll", aoRolar);
	}, []);

	useEffect(() => {
		if (!codigo) {
			setErro("Nenhum código de rastreio informado.");
			return;
		}
		let ativo = true;
		new RastreioService()
			.getByCodigo(codigo)
			.then((resultado) => ativo && setDados(resultado))
			.catch((e) => ativo && setErro(e.message));
		return () => {
			ativo = false;
		};
	}, [codigo]);

	const encontrou = Boolean(dados) && !erro;
	const ehItem = dados?.tipo === "item";

	// A galeria só vira seção quando existe: sem foto nenhuma sobraria uma tela
	// inteira em branco no meio do portal. Por isso as âncoras seguintes andam.
	const temGaleria = (dados?.distribuidora?.galeria?.length ?? 0) > 0;
	const idRastreio = temGaleria ? 3 : 2;
	const idCaminho = temGaleria ? 4 : 3;

	const descer = () => setSecao((atual) => Math.min(atual + 1, idCaminho));
	const subir = () => setSecao((atual) => Math.max(atual - 1, 0));

	return (
		<Box
			component="main"
			sx={{
				width: "100%",
				minHeight: "100vh",
				bgcolor: "#00A69A",
				color: "white",
				// As seções empilham e o fundo do hero é maior que a viewport:
				// sem isto sobra uma barra de rolagem horizontal na página toda.
				overflowX: "hidden",
			}}
		>
			<Grid
				container
				sx={{
					display: "flex",
					justifyContent: "space-between",
					position: "relative",
					width: "100%",
					m: 0,
				}}
			>
				{posicaoY > 400 && (
					<a
						href="#0"
						onClick={() => setSecao(0)}
						style={{ position: "fixed", bottom: "120px", right: "10px", color: "aqua", zIndex: 99 }}
					>
						<KeyboardDoubleArrowUpIcon sx={{ width: "40px", height: "40px" }} />
					</a>
				)}
				<a
					href={`#${secao}`}
					onClick={subir}
					style={{
						position: "fixed",
						bottom: "80px",
						right: "10px",
						color: "#00ba9d",
						textDecoration: "none",
						zIndex: 99,
					}}
				>
					<ArrowCircleUpIcon sx={{ width: "40px", height: "40px" }} />
				</a>
				<a
					href={`#${secao}`}
					onClick={descer}
					style={{ position: "fixed", bottom: "40px", right: "10px", color: "#00ba9d", zIndex: 99 }}
				>
					<ArrowCircleDownIcon sx={{ width: "40px", height: "40px" }} />
				</a>

				<Grid size={12} sx={styleFirstPage} id="0">
					<PrimeiraPagina nomeDistribuidora={dados?.distribuidora?.nome} />
				</Grid>

				{erro && (
					<Grid size={12} sx={{ ...styleBrancoRadial, textAlign: "center" }} id="1">
						<Typography variant="h4" sx={{ fontWeight: 700, color: "#009199", mb: 2 }}>
							{erro}
						</Typography>
						<Typography variant="body1">
							Confira se o código foi lido por inteiro a partir da etiqueta ou do
							romaneio.
						</Typography>
					</Grid>
				)}

				{/* Código só da venda: a remessa inteira, para escolher o item. */}
				{encontrou && !ehItem && (
					<Grid size={12} sx={styleBrancoRadial} id="1">
						<Box sx={{ maxWidth: 900, mx: "auto" }}>
							<Typography variant="h3" sx={{ fontWeight: 700, color: "#009199", mb: 1 }}>
								Remessa {dados.venda?.numero}
							</Typography>
							<Typography variant="body1" sx={{ mb: 3 }}>
								Esta remessa tem {dados.itens?.length ?? 0}{" "}
								{dados.itens?.length === 1 ? "item" : "itens"}. Escolha um para ver a
								origem.
							</Typography>
							{(dados.itens ?? []).map((item) => (
								<Box
									key={item.codigo}
									sx={{ bgcolor: "white", borderRadius: 2, p: 2, mb: 1.5, boxShadow: 1 }}
								>
									<Link
										href={`/rastreabilidade?id=${encodeURIComponent(item.codigo)}`}
										underline="none"
									>
										<Typography variant="h6" sx={{ fontWeight: 700, color: "#009199" }}>
											{item.produto ?? "Produto não informado"}
										</Typography>
									</Link>
									<Typography variant="body2" sx={{ color: "#666" }}>
										{[
											item.lote ? `Lote ${item.lote}` : null,
											item.volumes ? `${item.volumes} volume(s)` : null,
										]
											.filter(Boolean)
											.join(" · ")}
									</Typography>
								</Box>
							))}
						</Box>
					</Grid>
				)}

				{encontrou && ehItem && (
					<>
						<Grid size={12} sx={styleBrancoRadial} id="1">
							<SobreDistribuidora distribuidora={dados.distribuidora} />
						</Grid>
						{temGaleria && (
							<Grid size={12} sx={styleApresentacao} id="2">
								<ApresentacaoDistribuidora distribuidora={dados.distribuidora} />
							</Grid>
						)}
						<Grid size={12} sx={styleRastreioProduto} id={String(idRastreio)}>
							<RastreioProduto dados={dados} />
						</Grid>
						<Grid size={12} sx={styleCaminhoProduto} id={String(idCaminho)}>
							<CaminhoProduto dados={dados} />
						</Grid>
					</>
				)}
			</Grid>
		</Box>
	);
}

export default memo(Rastreabilidade);
